import { useParams, Link } from "wouter";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { format } from "date-fns";
import { useGetPost } from "@workspace/api-client";
import { useQuery } from "@tanstack/react-query";
import { ImageSlider } from "@/components/common/ImageSlider";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

export default function BlogDetail() {
  const { slug } = useParams();
  const { data: post, isLoading } = useGetPost(slug || "", { query: { enabled: !!slug } } as any);
  const { data: media } = useQuery<any[]>({
    queryKey: ["media", "POST", post?.id],
    queryFn: async () => {
      const res = await fetch(`/api/media/POST/${post?.id}`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!post?.id,
  });

  if (isLoading) {
    return <div className="pt-32 pb-20 px-4 text-center text-muted-foreground">Loading...</div>;
  }

  if (!post) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <h1 className="font-display text-4xl font-bold mb-4">Post Not Found</h1>
        <p className="text-muted-foreground mb-8">This article doesn't exist or has been removed.</p>
        <Link href="/blog" className="inline-flex items-center text-primary hover:underline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title={post.title}
        description={post.excerpt || post.content?.replace(/<[^>]+>/g, '').substring(0, 160)}
        image={post.coverImage}
        type="article"
      />
      <JsonLd schema={[
        {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "url": `https://acceleratebyte.com/blog/${slug}`,
          "description": post.excerpt || post.content?.replace(/<[^>]+>/g, '').substring(0, 160),
          "image": post.coverImage || "https://acceleratebyte.com/opengraph.jpg",
          "datePublished": post.createdAt,
          "dateModified": post.updatedAt || post.createdAt,
          "author": post.authorName
            ? { "@type": "Person", "name": post.authorName }
            : { "@id": "https://acceleratebyte.com/#organization" },
          "publisher": { "@id": "https://acceleratebyte.com/#organization" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://acceleratebyte.com/blog/${slug}` },
          "articleSection": post.category || "Technology",
          "wordCount": post.content?.split(/\s+/).length
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://acceleratebyte.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://acceleratebyte.com/blog" },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://acceleratebyte.com/blog/${slug}` }
          ]
        }
      ]} />
      <article className="pt-32 pb-24 px-4 container mx-auto max-w-3xl">
        <Link href="/blog" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
        </Link>

        <header className="mb-12 border-b border-border/50 pb-12">
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
            {post.category && (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {post.category}
              </span>
            )}
            <span className="flex items-center"><Calendar className="mr-1.5 h-3.5 w-3.5" /> {format(new Date(post.createdAt), 'MMM d, yyyy')}</span>
            {post.readTime > 0 && (
              <span className="flex items-center"><Clock className="mr-1.5 h-3.5 w-3.5" /> {post.readTime} min read</span>
            )}
          </div>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>

          {post.authorName && (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-bold text-sm">
                {post.authorName.charAt(0)}
              </div>
              <span className="font-medium">{post.authorName}</span>
            </div>
          )}

          {media && media.length > 0 && (
            <div className="mt-12">
              <ImageSlider images={media} />
            </div>
          )}
        </header>

        <div className="prose prose-invert prose-lg max-w-none mb-16 [&_*]:max-w-full break-words" dangerouslySetInnerHTML={{ __html: post.content }} />

        <footer className="flex items-center justify-between pt-8 border-t border-border/50">
          <div className="font-medium text-foreground">Share this article</div>
          <div className="flex gap-4">
            <button className="h-10 w-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors text-sm font-bold">
              X
            </button>
            <button className="h-10 w-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors text-sm font-bold">
              in
            </button>
          </div>
        </footer>
      </article>

      <section className="py-24 px-4 bg-muted/10 border-t border-border/50">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold mb-6">Want to implement these ideas?</h2>
          <p className="text-muted-foreground mb-8">Our elite engineering team can help you build your next product using the exact strategies outlined above.</p>
          <Link href="/contact" className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-8 text-base font-medium text-primary-foreground shadow transition-transform hover:scale-105">
            Work With Us
          </Link>
        </div>
      </section>
    </div>
  );
}
